import { LineChart, BarChart } from '@mui/x-charts'
import { Box } from '@mui/material'
import { nullSafeValueFormatter } from 'src/v3/utilities/charts'
import { LikertDataResponse } from '../scribe.slice'

interface LikertTimeSeriesChartProps {
  data: LikertDataResponse
  course?: any
}

export default function LikertTimeSeriesChart({
  data,
  course,
}: LikertTimeSeriesChartProps) {
  if (!data || data.length === 0) {
    let isLiveCourse = course?.kind === 'Live'

    return (
      <Box
        sx={{
          height: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        {isLiveCourse
          ? 'Analytics and Sentiment analysis is coming soon for live courses'
          : 'No survey data available'}
      </Box>
    )
  }

  let isSinglePeriod = data.length === 1

  // Single period - use horizontal bar chart
  if (isSinglePeriod) {
    let singleData = data[0]
    let barData = [
      {
        metric: 'Topics Thorough & Interesting',
        value: singleData.topicsThoroughAndInteresting,
      },
      {
        metric: 'Presenter Understanding',
        value: singleData.presenterGoodUnderstanding,
      },
      { metric: 'Video Quality', value: singleData.videoQualityProfessional },
      { metric: 'Career Benefits', value: singleData.benefitCareerGoals },
      {
        metric: 'Knowledge Impact',
        value: singleData.knowledgePositiveImpactPractice,
      },
      {
        metric: 'Activity Impact',
        value: singleData.activityPositiveImpactPractice,
      },
    ]

    return (
      <Box sx={{ height: '450px', width: '100%' }}>
        <BarChart
          dataset={barData}
          yAxis={[
            {
              scaleType: 'band',
              dataKey: 'metric',
            },
          ]}
          xAxis={[
            {
              min: 0,
              max: 5,
            },
          ]}
          series={[
            {
              dataKey: 'value',
              label: `Likert Averages - ${singleData.monthYear}`,
              valueFormatter: nullSafeValueFormatter,
            },
          ]}
          layout="horizontal"
          slotProps={{
            legend: {
              direction: 'row' as any,
              position: { vertical: 'bottom', horizontal: 'center' },
              sx: { padding: 10 },
            },
          }}
        />
      </Box>
    )
  }

  // Multiple periods - use line chart for trends
  // eslint-disable-next-line fp/no-mutating-methods
  let sortedData = [...data].reverse()

  return (
    <Box sx={{ height: '450px', width: '100%' }}>
      <LineChart
        dataset={sortedData as any}
        xAxis={[
          {
            scaleType: 'point',
            dataKey: 'monthYear',
          },
        ]}
        yAxis={[
          {
            min: 0,
            max: 5,
          },
        ]}
        series={[
          {
            dataKey: 'topicsThoroughAndInteresting',
            label: 'Topics Thorough & Interesting',
            curve: 'linear',
            showMark: true,
            valueFormatter: nullSafeValueFormatter,
          },
          {
            dataKey: 'presenterGoodUnderstanding',
            label: 'Presenter Understanding',
            curve: 'linear',
            showMark: true,
            valueFormatter: nullSafeValueFormatter,
          },
          {
            dataKey: 'videoQualityProfessional',
            label: 'Video Quality',
            curve: 'linear',
            showMark: true,
            valueFormatter: nullSafeValueFormatter,
          },
          {
            dataKey: 'benefitCareerGoals',
            label: 'Career Benefits',
            curve: 'linear',
            showMark: true,
            valueFormatter: nullSafeValueFormatter,
          },
          {
            dataKey: 'knowledgePositiveImpactPractice',
            label: 'Knowledge Impact',
            curve: 'linear',
            showMark: true,
            valueFormatter: nullSafeValueFormatter,
          },
          {
            dataKey: 'activityPositiveImpactPractice',
            label: 'Activity Impact',
            curve: 'linear',
            showMark: true,
            valueFormatter: nullSafeValueFormatter,
          },
        ]}
        slotProps={{
          legend: {
            direction: 'row' as any,
            position: { vertical: 'bottom', horizontal: 'center' },
            sx: { padding: 10 },
          },
        }}
      />
    </Box>
  )
}
